import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Trash2,
  RefreshCw,
  CheckCircle2,
  Inbox,
  Download,
  Undo2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { useAdminList, API, authHeaders, downloadCsv } from "./adminApi";

const FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "pending", label: "En attente" },
  { key: "done", label: "Traitées" },
];

export default function ContactsAdmin({ token, onUnauthorized }) {
  const { data, loading, refresh, setData } = useAdminList(
    token,
    "/admin/contacts",
    onUnauthorized,
  );
  const [filter, setFilter] = useState("pending");

  const filtered = useMemo(() => {
    if (filter === "all") return data;
    if (filter === "done") return data.filter((c) => c.status === "done");
    return data.filter((c) => c.status !== "done");
  }, [data, filter]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: authHeaders(token) });
      setData((d) => d.filter((c) => c.id !== id));
      toast.success("Demande supprimée");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === "done" ? "pending" : "done";
    try {
      await axios.patch(
        `${API}/admin/contacts/${c.id}/status`,
        { status: newStatus },
        { headers: authHeaders(token) },
      );
      setData((d) => d.map((x) => (x.id === c.id ? { ...x, status: newStatus } : x)));
      toast.success(newStatus === "done" ? "Marquée comme traitée" : "Remise en attente");
    } catch {
      toast.error("Erreur");
    }
  };

  const exportCsv = () =>
    downloadCsv(token, "/admin/export/contacts.csv", "contacts-kreuse.csv");

  return (
    <div data-testid="admin-contacts-page" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-semibold text-[#1A2E1C]">
            Demandes de contact
          </h2>
          <p className="text-[#5C705E] mt-1">{data.length} demande(s) au total</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={refresh}
            variant="outline"
            className="rounded-full border-[#E5EBE5] text-[#1B5E20] hover:bg-[#E8F5E9]"
            data-testid="contacts-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button
            onClick={exportCsv}
            variant="outline"
            className="rounded-full border-[#E5EBE5] text-[#1B5E20] hover:bg-[#E8F5E9]"
            data-testid="contacts-export-csv"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </header>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`contacts-filter-${f.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === f.key
                ? "bg-[#1B5E20] text-white shadow"
                : "bg-white border border-[#E5EBE5] text-[#5C705E] hover:text-[#1B5E20]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {(() => {
        if (loading) {
          return <div className="text-center py-20 text-[#5C705E]">Chargement…</div>;
        }
        if (filtered.length === 0) {
          return (
            <div className="text-center py-20 border-2 border-dashed border-[#E5EBE5] rounded-3xl">
              <Inbox className="w-16 h-16 text-[#A5D6A7] mx-auto mb-4" />
              <p className="text-[#5C705E]">Aucune demande dans cette catégorie.</p>
            </div>
          );
        }
        return (
          <div className="grid gap-4" data-testid="admin-contacts-list">
            {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`admin-contact-${c.id}`}
              className={`bg-white border rounded-3xl p-6 hover:shadow-md transition-shadow ${
                c.status === "done" ? "border-[#C8E6C9] bg-[#F8FBF8]" : "border-[#E5EBE5]"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {c.status === "done" && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#1B5E20] bg-[#E8F5E9] px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" />
                        Traité
                      </span>
                    )}
                  </div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#1A2E1C]">
                    {c.name}
                  </h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#5C705E]">
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-[#1B5E20]">
                      <Mail className="w-3.5 h-3.5" />
                      {c.email}
                    </a>
                    {c.phone && (
                      <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-[#1B5E20]">
                        <Phone className="w-3.5 h-3.5" />
                        {c.phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {c.service && (
                    <span className="px-3 py-1 bg-[#E8F5E9] text-[#1B5E20] rounded-full text-xs font-medium">
                      {c.service}
                    </span>
                  )}
                  <button
                    onClick={() => toggleStatus(c)}
                    data-testid={`contact-toggle-status-${c.id}`}
                    className="p-2 rounded-full hover:bg-[#E8F5E9]"
                    title={c.status === "done" ? "Marquer en attente" : "Marquer traitée"}
                  >
                    {c.status === "done" ? (
                      <Undo2 className="w-4 h-4 text-[#5C705E]" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-[#1B5E20]" />
                    )}
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`admin-delete-${c.id}`}
                    className="text-[#D32F2F]/70 hover:text-[#D32F2F] p-2 rounded-full hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-[#1A2E1C] whitespace-pre-line border-t border-[#E5EBE5] pt-4">
                {c.message}
              </p>
              <div className="mt-4 text-xs text-[#5C705E]">
                Reçu le{" "}
                {new Date(c.created_at).toLocaleString("fr-FR", {
                  dateStyle: "long",
                  timeStyle: "short",
                })}
              </div>
            </div>
          ))}
        </div>
        );
      })()}
    </div>
  );
}
